import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  AlertTriangle, 
  RefreshCw, 
  Loader2, 
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Target,
  Lightbulb,
  Sparkles
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { SEO } from '@/components/SEO';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface Scenario {
  name: string;
  runwayDays: number;
  endBalance: number;
  assumption: string;
  color: string;
}

interface ForecastData {
  day: string;
  base: number;
  downside: number;
  worst: number;
}

interface RecommendedAction {
  title: string;
  impact: string;
  priority: 'high' | 'medium' | 'low';
}

export default function Runway() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canAccess } = usePlanLimits();
  const hasForecast = canAccess('hasCashFlowForecast');
  
  const [loading, setLoading] = useState(true);
  const [runwayDays, setRunwayDays] = useState(0);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [forecastData, setForecastData] = useState<ForecastData[]>([]);
  const [weeklyChange, setWeeklyChange] = useState({ days: 0, balance: 0 });
  const [recommendations, setRecommendations] = useState<RecommendedAction[]>([]);
  const [trend, setTrend] = useState<'up' | 'down' | 'stable'>('stable');

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const { data: transactions } = await supabase
        .from('bank_transactions')
        .select('amount, date')
        .eq('user_id', user.id)
        .order('date', { ascending: true });

      if (transactions && transactions.length > 0) {
        const balance = transactions.reduce((acc, t) => acc + t.amount, 0);
        setCurrentBalance(balance);

        // Calculate daily averages
        const inflows = transactions.filter(t => t.amount > 0);
        const outflows = transactions.filter(t => t.amount < 0);
        const avgDailyInflow = inflows.reduce((acc, t) => acc + t.amount, 0) / Math.max(transactions.length, 30);
        const avgDailyOutflow = Math.abs(outflows.reduce((acc, t) => acc + t.amount, 0)) / Math.max(transactions.length, 30);
        const netDaily = avgDailyInflow - avgDailyOutflow;

        // Calculate runway
        let runway = 999;
        if (netDaily < 0 && balance > 0) {
          runway = Math.floor(balance / Math.abs(netDaily));
        }
        setRunwayDays(Math.min(runway, 999));

        // Determine trend
        setTrend(netDaily > 1000 ? 'up' : netDaily < -1000 ? 'down' : 'stable');

        // Generate scenarios
        const baseScenario: Scenario = {
          name: 'Base Case',
          runwayDays: runway,
          endBalance: balance + (netDaily * 30),
          assumption: 'Current trends continue',
          color: 'hsl(var(--primary))',
        };

        const downsideScenario: Scenario = {
          name: 'Downside',
          runwayDays: Math.floor(runway * 0.7),
          endBalance: balance + (netDaily * 0.7 * 30),
          assumption: '30% lower collections',
          color: 'hsl(var(--warning))',
        };

        const worstScenario: Scenario = {
          name: 'Worst Case',
          runwayDays: Math.floor(runway * 0.5),
          endBalance: balance + (netDaily * 0.5 * 30),
          assumption: '50% lower collections',
          color: 'hsl(var(--destructive))',
        };

        setScenarios([baseScenario, downsideScenario, worstScenario]);

        // Generate forecast data
        const forecast: ForecastData[] = [];
        let baseBalance = balance;
        let downsideBalance = balance;
        let worstBalance = balance;

        for (let i = 1; i <= 30; i++) {
          const variance = 0.9 + Math.random() * 0.2;
          baseBalance += netDaily * variance;
          downsideBalance += netDaily * 0.7 * variance;
          worstBalance += netDaily * 0.5 * variance;

          forecast.push({
            day: `Day ${i}`,
            base: Math.max(0, baseBalance),
            downside: Math.max(0, downsideBalance),
            worst: Math.max(0, worstBalance),
          });
        }
        setForecastData(forecast);

        // Weekly change (mock for now)
        setWeeklyChange({
          days: Math.floor(Math.random() * 10) - 5,
          balance: Math.floor((Math.random() - 0.5) * 50000),
        });

        // Generate recommendations
        const recs: RecommendedAction[] = [];
        if (runway < 30) {
          recs.push({
            title: 'Accelerate receivables collection',
            impact: `Could add ${Math.floor(runway * 0.3)} days to runway`,
            priority: 'high',
          });
        }
        if (avgDailyOutflow > avgDailyInflow * 1.2) {
          recs.push({
            title: 'Review recurring expenses',
            impact: 'Outflows exceed inflows by 20%+',
            priority: 'high',
          });
        }
        recs.push({
          title: 'Negotiate payment terms with top vendors',
          impact: 'Extend payables by 15-30 days',
          priority: 'medium',
        });
        setRecommendations(recs.slice(0, 3));
      } else {
        // Sample data
        setCurrentBalance(500000);
        setRunwayDays(45);
        setTrend('stable');
        setScenarios([
          { name: 'Base Case', runwayDays: 45, endBalance: 520000, assumption: 'Current trends continue', color: 'hsl(var(--primary))' },
          { name: 'Downside', runwayDays: 32, endBalance: 380000, assumption: '30% lower collections', color: 'hsl(var(--warning))' },
          { name: 'Worst Case', runwayDays: 22, endBalance: 250000, assumption: '50% lower collections', color: 'hsl(var(--destructive))' },
        ]);
        setWeeklyChange({ days: -3, balance: -25000 });
        setRecommendations([
          { title: 'Import your bank transactions', impact: 'Get accurate runway predictions', priority: 'high' },
          { title: 'Connect Tally for real-time data', impact: 'Improve forecast accuracy', priority: 'medium' },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasForecast) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user, hasForecast]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const formatCompact = (amount: number) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' ? 'text-success' : trend === 'down' ? 'text-destructive' : 'text-muted-foreground';

  if (!hasForecast) {
    return (
      <div className="min-h-full bg-background p-4 md:p-8 flex items-center justify-center">
        <SEO title="Runway - Sentinel" description="Cash flow runway predictions" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="max-w-md mx-auto p-10 text-center rounded-3xl">
            <div className="w-20 h-20 rounded-3xl bg-primary/10 mx-auto mb-6 flex items-center justify-center">
              <Target className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Unlock Runway Predictions</h2>
            <p className="text-muted-foreground mb-8">
              Upgrade to Pro to access cash flow forecasting and runway predictions.
            </p>
            <Button onClick={() => navigate('/pricing')} className="rounded-2xl px-8">Upgrade to Pro</Button>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background">
      <SEO title="Runway - Sentinel" description="Cash flow runway predictions" />

      <div className="p-4 md:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-3xl bg-gradient-primary shadow-glow">
              <Target className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Runway</h1>
              <p className="text-sm text-muted-foreground">Your financial runway at a glance</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="rounded-2xl">
            <RefreshCw className={cn('h-4 w-4 mr-1', loading && 'animate-spin')} />
            Refresh
          </Button>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Hero Metric */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="p-8 md:p-12 bg-gradient-to-br from-primary/5 via-background to-accent/5 border-primary/20 rounded-3xl">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-3">Cash Runway</p>
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <span className={cn(
                      'text-7xl md:text-9xl font-bold tracking-tight',
                      runwayDays < 30 ? 'text-destructive' : runwayDays < 60 ? 'text-warning' : 'text-foreground'
                    )}>
                      {runwayDays === 999 ? '∞' : runwayDays}
                    </span>
                    <span className="text-2xl md:text-3xl text-muted-foreground">days</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 flex-wrap">
                    <Badge variant="secondary" className={cn("rounded-xl px-4 py-1.5", trendColor)}>
                      <TrendIcon className="h-4 w-4 mr-1.5" />
                      {trend === 'up' ? 'Improving' : trend === 'down' ? 'Declining' : 'Stable'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Balance: {formatCurrency(currentBalance)}
                    </span>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Runway Warning */}
            {runwayDays < 60 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                <Alert className="border-destructive/50 bg-destructive/10 rounded-3xl">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  <AlertDescription className="text-destructive ml-2">
                    <strong>Cash Runway Alert:</strong> At current burn rate, you have approximately {runwayDays} days of runway.
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}

            {/* Scenario Cards */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              {scenarios.map((scenario, idx) => (
                <Card key={scenario.name} className="p-6 rounded-3xl hover:shadow-card-hover transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-foreground">{scenario.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{scenario.assumption}</p>
                    </div>
                    <Badge
                      variant={scenario.name === 'Base Case' ? 'default' : scenario.name === 'Downside' ? 'secondary' : 'destructive'}
                      className="rounded-xl"
                    >
                      {scenario.runwayDays} days
                    </Badge>
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {formatCompact(scenario.endBalance)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">30-day projected balance</p>
                </Card>
              ))}
            </motion.div>

            {/* What Changed */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              <Card className="p-6 rounded-3xl">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  What Changed This Week
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-14 h-14 rounded-2xl flex items-center justify-center",
                      weeklyChange.days >= 0 ? "bg-success/10" : "bg-destructive/10"
                    )}>
                      {weeklyChange.days >= 0 ? (
                        <ArrowUpRight className="h-7 w-7 text-success" />
                      ) : (
                        <ArrowDownRight className="h-7 w-7 text-destructive" />
                      )}
                    </div>
                    <div>
                      <div className={cn(
                        'text-3xl font-bold',
                        weeklyChange.days >= 0 ? 'text-success' : 'text-destructive'
                      )}>
                        {weeklyChange.days >= 0 ? '+' : ''}{weeklyChange.days} days
                      </div>
                      <p className="text-sm text-muted-foreground">Runway change</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-14 h-14 rounded-2xl flex items-center justify-center",
                      weeklyChange.balance >= 0 ? "bg-success/10" : "bg-destructive/10"
                    )}>
                      {weeklyChange.balance >= 0 ? (
                        <ArrowUpRight className="h-7 w-7 text-success" />
                      ) : (
                        <ArrowDownRight className="h-7 w-7 text-destructive" />
                      )}
                    </div>
                    <div>
                      <div className={cn(
                        'text-3xl font-bold',
                        weeklyChange.balance >= 0 ? 'text-success' : 'text-destructive'
                      )}>
                        {weeklyChange.balance >= 0 ? '+' : ''}{formatCompact(weeklyChange.balance)}
                      </div>
                      <p className="text-sm text-muted-foreground">Balance change</p>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Chart */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
              <Card className="p-6 rounded-3xl">
                <CardHeader className="px-0 pt-0 pb-6">
                  <CardTitle className="text-lg">30-Day Projection</CardTitle>
                  <CardDescription>
                    Base, downside, and worst-case scenarios
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={forecastData}>
                        <defs>
                          <linearGradient id="colorBase" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis
                          dataKey="day"
                          stroke="hsl(var(--muted-foreground))"
                          tick={{ fontSize: 10 }}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={formatCompact}
                          tick={{ fontSize: 10 }}
                          width={50}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--popover))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '1rem',
                            fontSize: '12px',
                          }}
                          formatter={(value: number, name: string) => [formatCurrency(value), name]}
                        />
                        <Area type="monotone" dataKey="base" stroke="hsl(var(--primary))" strokeWidth={2} fillOpacity={1} fill="url(#colorBase)" name="Base" />
                        <Area type="monotone" dataKey="downside" stroke="hsl(var(--warning))" strokeWidth={1.5} fillOpacity={0} strokeDasharray="5 5" name="Downside" />
                        <Area type="monotone" dataKey="worst" stroke="hsl(var(--destructive))" strokeWidth={1} fillOpacity={0} strokeDasharray="3 3" name="Worst" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Recommendations */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
              <Card className="p-6 rounded-3xl">
                <h3 className="font-semibold text-foreground mb-5 flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-warning" />
                  Recommended Actions
                </h3>
                <div className="space-y-3">
                  {recommendations.map((rec, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        'p-5 rounded-2xl border',
                        rec.priority === 'high' ? 'border-destructive/30 bg-destructive/5' :
                        rec.priority === 'medium' ? 'border-warning/30 bg-warning/5' : 'border-border'
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium text-foreground">{rec.title}</div>
                          <p className="text-sm text-muted-foreground mt-1">{rec.impact}</p>
                        </div>
                        <Badge
                          variant={rec.priority === 'high' ? 'destructive' : rec.priority === 'medium' ? 'secondary' : 'outline'}
                          className="rounded-xl text-xs"
                        >
                          {rec.priority}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
